import type {
  TimesheetGroupBy,
  GroupingDimensionConfig,
} from '../types/timesheet.types';

/**
 * Registry of all available timesheet grouping modes.
 *
 * To add a new grouping dimension (e.g., "by Epic"):
 * 1. Add the new value to TimesheetGroupBy union type in timesheet.types.ts
 * 2. Add a new entry here
 * 3. Add a tree builder case in timesheet-tree-builder.ts
 * 4. (Optional) Add a backend aggregation case if the new dimension
 *    requires a different GROUP BY clause.
 */
export const GROUPING_REGISTRY: readonly GroupingDimensionConfig[] = [
  // ── User Timesheets ──────────────────────────────────────────────────────
  {
    key: 'summary',
    label: 'Summary',
    description: 'One row per user with aggregated daily hours',
    category: 'user_timesheets',
    viewMode: 'timeline',
    hierarchical: false,
    backendGroupBy: 'user',
  },
  {
    key: 'board',
    label: 'by Board',
    description: 'Users as parents, boards as children',
    category: 'user_timesheets',
    viewMode: 'timeline',
    hierarchical: true,
    backendGroupBy: 'board',
  },
  {
    key: 'item',
    label: 'by Item',
    description: 'Users as parents, items as children',
    category: 'user_timesheets',
    viewMode: 'timeline',
    hierarchical: true,
    backendGroupBy: 'item',
  },

  // ── Board Timesheets ────────────────────────────────────────────────────
  {
    key: 'board_summary',
    label: 'Summary',
    description: 'One row per board with aggregated daily hours',
    category: 'board_timesheets',
    viewMode: 'timeline',
    hierarchical: false,
    backendGroupBy: 'board_summary',
  },
  {
    key: 'board_user',
    label: 'by User',
    description: 'Boards as parents, users as children',
    category: 'board_timesheets',
    viewMode: 'timeline',
    hierarchical: true,
    backendGroupBy: 'board_user',
  },

  // ── Progress Reports ──────────────────────────────────────────────────────
  {
    key: 'progress_timesheet',
    label: 'Timesheet Progress',
    description: 'Logged hours vs required capacity per user',
    category: 'progress_reports',
    viewMode: 'progress',
    hierarchical: false,
    backendGroupBy: 'user',
  },
  {
    key: 'progress_utilization',
    label: 'Resource Utilization',
    description: 'Planned hours from boards vs required capacity per user',
    category: 'progress_reports',
    viewMode: 'progress',
    hierarchical: false,
    backendGroupBy: 'user',
  },
  {
    key: 'progress_planned_vs_actual',
    label: 'Planned vs Actual',
    description: 'Logged hours vs planned hours per user',
    category: 'progress_reports',
    viewMode: 'progress',
    hierarchical: false,
    backendGroupBy: 'user',
  },

  // ── Timesheet Reports ─────────────────────────────────────────────────────
  {
    key: 'report_logged_time',
    label: 'Logged Time',
    description: 'Hierarchical report with configurable grouping by user, board, item, and category',
    category: 'timesheet_reports',
    viewMode: 'report',
    hierarchical: true,
    backendGroupBy: 'user',
  },

  // ── Approvals ─────────────────────────────────────────────────────────────
  {
    key: 'approval_pending',
    label: 'Pending Approvals',
    description: 'Timesheets awaiting your approval',
    category: 'approvals',
    viewMode: 'timeline', // Not used for rendering; ApproverView handles it
    hierarchical: false,
    backendGroupBy: 'user',
  },
  {
    key: 'approval_history',
    label: 'Approval History',
    description: 'Past approval decisions',
    category: 'approvals',
    viewMode: 'timeline', // Not used for rendering; ApproverView handles it
    hierarchical: false,
    backendGroupBy: 'user',
  },
] as const;

/**
 * Lookup a grouping config by key.
 */
export function getGroupingConfig(
  key: TimesheetGroupBy,
): GroupingDimensionConfig {
  const config = GROUPING_REGISTRY.find((g) => g.key === key);
  if (!config) throw new Error(`Unknown grouping mode: ${key}`);
  return config;
}

/**
 * Group registry entries by category for the sidebar.
 */
export function getGroupedTemplates(): Record<
  string,
  { label: string; items: GroupingDimensionConfig[] }
> {
  return {
    user_timesheets: {
      label: 'User timesheets',
      items: GROUPING_REGISTRY.filter(
        (g) => g.category === 'user_timesheets',
      ) as GroupingDimensionConfig[],
    },
    board_timesheets: {
      label: 'Board timesheets',
      items: GROUPING_REGISTRY.filter(
        (g) => g.category === 'board_timesheets',
      ) as GroupingDimensionConfig[],
    },
    progress_reports: {
      label: 'Progress',
      items: GROUPING_REGISTRY.filter(
        (g) => g.category === 'progress_reports',
      ) as GroupingDimensionConfig[],
    },
    timesheet_reports: {
      label: 'Reports',
      items: GROUPING_REGISTRY.filter(
        (g) => g.category === 'timesheet_reports',
      ) as GroupingDimensionConfig[],
    },
    approvals: {
      label: 'Approvals',
      items: GROUPING_REGISTRY.filter(
        (g) => g.category === 'approvals',
      ) as GroupingDimensionConfig[],
    },
  };
}
